/**
 * GMX (sağlayıcı id: gmxbox) — TEK sayfa: stok, kimlik doğrulama, site başına
 * satışlar ve mailbox başına "hangi siteler için satıldı" detayı, import —
 * hepsi burada (D-247). Mailbox kendi adresiyle satılır; alias yoktur, satış
 * hakkı (mailbox, site) bazındadır.
 *
 * 2026-10-23 (D-263, operatör D13/D14) — GMX hesapları yeniden gmx.com alias
 * hattına (gmx.com Parent Mailler, mail.com mantığı) taşındı; gmxbox kayıtlı
 * kalır ama PASİF / salt okunur olur. Pasiflik SUNUCUDAN okunur: Genel Bakış
 * (GET /admin/overview) gmxbox için `passive` VEYA `salesClosed` derse sayfa
 * salt okunurdur (uyarı bandı; Import / TXT ve mailbox işlemleri gizli). Genel Bakış okunana kadar
 * (ya da okunamazsa) statik katalog değeri (gmxbox pasif) güvenli taraftır.
 * Asıl sınır sunucudur: pasif hatta her yazma isteği 409 provider_passive alır.
 */
import { useCallback, useEffect, useState } from 'react';

import { useProviderLines } from '../../api/overview.js';
import { resolveProviderLine } from '../../providers/catalog.js';
import { useCan } from '../../session/can.js';
import { Banner } from '../../ui/index.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import { MailboxUnitDashboard } from '../mailbox-unit/MailboxUnitDashboard.js';
import { MailboxUnitManager } from '../mailbox-unit/MailboxUnitManager.js';
import {
  availableShare,
  fetchGmxboxSiteUsage,
  type GmxboxSiteUsage,
} from './siteUsage.js';

import '../../styles/mailboxunit.mobile.css';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: GmxboxSiteUsage }
  | { readonly kind: 'error'; readonly message: string };



export function GmxboxPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const canManageCap = useCan('provider:manage_inventory');
  // 2026-10-23 (D-263) — server-driven read-only mode (passive OR sales-closed);
  // the static catalogue value is the fail-safe until the overview is read.
  const providerLines = useProviderLines();
  const line = resolveProviderLine('gmxbox', providerLines.lines);
  const readOnly = line.struck;
  const canManage = canManageCap && !readOnly;
  const [showImport, setShowImport] = useState(false);

  // `background`: a refresh after a manager mutation keeps the page (and so
  // the manager with its page / filters / feedback) mounted instead of
  // flipping to the loading state, which unmounted it and reset it to page 1.
  const load = useCallback(async (signal?: AbortSignal, opts?: { readonly background?: boolean }) => {
    if (opts?.background === true) setState((prev) => (prev.kind === 'ok' ? prev : { kind: 'loading' }));
    else setState({ kind: 'loading' });
    const res = await fetchGmxboxSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);


  return (
    <section className="mbu-dashboard" data-testid="gmxbox-dashboard">
      <header className="gmxbox-head mbu-head">
        <div>
          <h1>GMX — stok, kimlik ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelince o çift
            herkese kalıcı kapanır; posta gelmezse mailbox hemen başka müşterilere
            satılabilir, aynı müşteriye aynı site için bir daha satılmaz (D-247).
            Mailbox başka siteler için satılmaya devam eder. Bir mailbox,
            kimliği doğrulanmadan satılmaz. Bu sayfadaki mailbox'lar eski GMX
            parent hesaplarıdır (kendi adresleriyle satılır); yeni hesap
            "Import / TXT" ile eklenir.
          </p>
        </div>
        <div className="gmxbox-head-actions mbu-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowImport(true)}
              data-testid="gmxbox-import"
              title="email:şifre satırları — mailbox'ın kendisi unit olarak eklenir; şifre ':' içerebilir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={() => void load()} data-testid="gmxbox-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="gmxbox"
          providerLabel="GMX"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {readOnly && line.source === 'server' ? (
        <Banner
          tone="warn"
          title={line.reason === 'sales_closed' ? 'GMX mailbox hattı satışta değil' : 'GMX mailbox hattı pasif'}
          testId="gmxbox-passive-banner"
        >
          GMX hesapları gmx.com alias hattına (gmx.com Parent Mailler) taşındı; bu hat satış yapmaz ve sayfa salt
          okunurdur. Stok, satış geçmişi ve mailbox listesi görüntülenebilir; import ve mailbox işlemleri gizlidir
          {line.reason === 'passive' ? ' (sunucu da reddeder: provider_passive)' : ''}.
          Yeni GMX hesabı gmx.com Parent Mailler sayfasından eklenir.
        </Banner>
      ) : null}
      {readOnly && line.source === 'catalog' && providerLines.unavailable ? (
        <Banner tone="info" title="Hat durumu okunamadı" testId="gmxbox-line-state-unknown">
          Genel Bakış verisi alınamadı; sayfa güvenli tarafta salt okunur gösteriliyor (import ve mailbox işlemleri gizli).
        </Banner>
      ) : null}

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="gmxbox-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="gmxbox-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="stat" data-testid="kpi-leased">
                <div className="stat-label">Kiralamada</div>
                <div className="stat-value">{state.data.units.leased}</div>
                <div className="stat-meta">şu an müşteride</div>
              </div>
              <div className="stat" data-testid="kpi-verified">
                <div className="stat-label">Kimlik doğrulandı</div>
                <div className="stat-value">{state.data.probe?.verified ?? '—'}</div>
                <div className="stat-meta">
                  {state.data.probe?.pending ?? 0} bekliyor · {state.data.probe?.quarantined ?? 0} karantina
                </div>
              </div>
              <div className="stat" data-testid="kpi-accounts">
                <div className="stat-label">Hesap</div>
                <div className="stat-value">{state.data.accounts}</div>
                <div className="stat-meta">GMX mailbox hesabı</div>
              </div>
          </div>

          <MailboxUnitDashboard providerId="gmxbox" providerLabel="GMX" />

          <MailboxUnitManager
            providerId="gmxbox"
            providerLabel="GMX"
            readOnly={readOnly}
            onChanged={() => void load(undefined, { background: true })}
          />

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="gmxbox-footnote" data-testid="gmxbox-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
                Import edilen bir mailbox, kimlik doğrulaması (dakikada 10 hesap) geçince satışa açılır.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
